import { Check, Shield, Zap, Clock, ArrowRight } from "lucide-react";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import TestimonialsPanel from "@/components/premium/TestimonialsPanel";
import ComingSoonPanel from "@/components/premium/ComingSoonPanel";
import { trackButtonClick } from "@/utils/tracking";
import { trackCustomEvent } from "@/utils/analyticsTracker";
import { Badge } from "@/components/ui/badge";

const Pricing = () => {
  const { user, isSubscribed, subscriptionDetails } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const premiumFeatures = [
    "Access to all ML system design resources",
    "Real interview questions from FAANG companies",
    "System design templates and examples",
    "Regular content updates",
    "Architecture diagrams and code snippets",
    "Performance optimization guides",
  ];

  const freeFeatures = [
    "Limited ML system design articles",
    "Basic interview tips",
    "Community access",
  ];

  const handleSubscribe = async () => {
    trackButtonClick("pricing_subscribe_button", {
      isSubscribed: isSubscribed,
      isLoggedIn: !!user,
    });

    if (isSubscribed) {
      toast({
        title: "Already Subscribed",
        description: "You already have an active subscription.",
        variant: "default",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Login Required",
        description: "Please log in to subscribe to HelloML Premium.",
        variant: "destructive",
      });
      window.location.href = "/login?returnTo=/pricing";
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } =
        await supabase.functions.invoke("create-checkout");

      if (error) throw error;

      if (data?.url) {
        trackCustomEvent("stripe_checkout_redirect", {
          extraData: { checkoutUrl: data.url },
        });
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      toast({
        title: "Error",
        description:
          "There was a problem setting up the checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-grow py-16 md:py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <Badge variant="secondary" className="mb-4">
              <Zap className="h-3 w-3 mr-1" />
              Limited Time Offer
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              Unlock premium ML system design resources with a monthly subscription.
            </p>
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4 text-primary" />
                <span>30-Day Money-Back Guarantee</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4 text-primary" />
                <span>Cancel Anytime</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Left side - Testimonials Panel */}
            <div>
              <TestimonialsPanel />
            </div>

            {/* Middle - Pricing Card */}
            <div className="lg:col-span-1">
              <div className="max-w-md mx-auto bg-card rounded-xl border-2 border-primary shadow-lg overflow-hidden relative">
                {/* Popular badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground px-4 py-1">
                    Most Popular
                  </Badge>
                </div>
                
                <div className="p-8 text-center pt-10">
                  <h2 className="text-2xl font-bold mb-2">Premium Access</h2>
                  
                  {/* Price with discount */}
                  <div className="mb-2">
                    <span className="text-lg text-muted-foreground line-through">$49</span>
                    <span className="text-4xl font-bold ml-2">$29</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <p className="text-sm text-primary font-medium mb-4">
                    Save 40% - Limited time offer!
                  </p>
                  
                  <p className="text-muted-foreground mb-6">
                    Full access to all HelloML resources to help you ace ML
                    system design interviews.
                  </p>

                  {isSubscribed ? (
                    <div className="bg-primary/10 text-primary py-3 px-4 rounded-md mb-6">
                      <p className="font-medium">You're already subscribed!</p>
                      {subscriptionDetails?.subscription_end && (
                        <p className="text-sm">
                          Access until:{" "}
                          {new Date(
                            subscriptionDetails.subscription_end,
                          ).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <Button
                        className="w-full mb-4"
                        size="lg"
                        onClick={handleSubscribe}
                        disabled={isLoading || isSubscribed}
                      >
                        {isLoading ? "Processing..." : "Start Premium Now"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>

                      {/* Money-back guarantee */}
                      <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-primary/5 rounded-lg">
                        <Shield className="h-5 w-5 text-primary" />
                        <span className="text-sm font-medium">30-Day Money-Back Guarantee</span>
                      </div>

                      {subscriptionDetails?.reason && user && (
                        <div className="text-amber-600 text-sm mb-4">
                          Note: {subscriptionDetails.reason}
                        </div>
                      )}
                    </>
                  )}

                  <p className="text-sm text-muted-foreground">
                    Cancel anytime. No long-term commitment required.
                  </p>
                </div>

                <div className="bg-muted p-8 border-t">
                  <h3 className="font-medium mb-4">What's included:</h3>
                  <ul className="space-y-3">
                    {premiumFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Right side - Coming Soon Panel */}
            <div>
              <ComingSoonPanel />
            </div>
          </div>

          {/* Feature Comparison Table */}
          <div className="max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Free vs Premium
            </h2>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="font-medium p-3">Feature</div>
              <div className="font-medium p-3 text-center">Free</div>
              <div className="font-medium p-3 text-center bg-primary/10 rounded-t-lg">Premium</div>
              
              <div className="p-3 border-t">ML System Design Articles</div>
              <div className="p-3 border-t text-center text-muted-foreground">Limited</div>
              <div className="p-3 border-t text-center bg-primary/5"><Check className="h-4 w-4 mx-auto text-primary" /></div>
              
              <div className="p-3 border-t">FAANG Interview Questions</div>
              <div className="p-3 border-t text-center text-muted-foreground">—</div>
              <div className="p-3 border-t text-center bg-primary/5"><Check className="h-4 w-4 mx-auto text-primary" /></div>
              
              <div className="p-3 border-t">Architecture Templates</div>
              <div className="p-3 border-t text-center text-muted-foreground">—</div>
              <div className="p-3 border-t text-center bg-primary/5"><Check className="h-4 w-4 mx-auto text-primary" /></div>
              
              <div className="p-3 border-t">Weekly Content Updates</div>
              <div className="p-3 border-t text-center text-muted-foreground">—</div>
              <div className="p-3 border-t text-center bg-primary/5"><Check className="h-4 w-4 mx-auto text-primary" /></div>
              
              <div className="p-3 border-t">Code Snippets & Diagrams</div>
              <div className="p-3 border-t text-center text-muted-foreground">—</div>
              <div className="p-3 border-t text-center bg-primary/5 rounded-b-lg"><Check className="h-4 w-4 mx-auto text-primary" /></div>
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              {[
                {
                  question: "Is there a money-back guarantee?",
                  answer:
                    "Yes! We offer a 30-day money-back guarantee. If you're not satisfied with your subscription, contact us within 30 days for a full refund, no questions asked.",
                },
                {
                  question: "How does the monthly subscription work?",
                  answer:
                    "Your subscription renews automatically each month at $29. You can cancel anytime from your account settings, and you'll retain access until the end of your current billing period.",
                },
                {
                  question: "How often is new content added?",
                  answer:
                    "We add new ML system design resources, interview questions, and templates every week to keep our content fresh and relevant.",
                },
                {
                  question: "Is there a free trial?",
                  answer:
                    "We offer select free resources on our Learn page. These provide a good sample of the quality and depth of our premium content. Plus, with our 30-day money-back guarantee, you can try premium risk-free!",
                },
                {
                  question: "Are there team or company subscriptions?",
                  answer:
                    "We offer special rates for teams and companies. Please contact us directly for more information on team pricing.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b pb-6">
                  <h3 className="text-lg font-medium mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
